import React, { useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardHeader, CardContent } from './ui/card';
import { CheckCircle, FileText, Activity, Search, Users, ArrowRight, Shield, Globe, Target } from 'lucide-react';
import SEOHead from './SEOHead';
import { siteContent } from '../data/mockData';

const iconComponents = {
  CheckCircle,
  FileText,
  Activity,
  Search,
  Users,
  Shield,
  Globe,
  Target
};

const Services = ({ currentSection, onNavigate }) => {
  const content = siteContent.en;
  
  useEffect(() => {
    if (currentSection) {
      setTimeout(() => {
        const element = document.getElementById(currentSection);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [currentSection]);
  
  const scrollToContact = () => {
    if (onNavigate) {
      onNavigate('contact');
    }
  };

  return (
    <div className="py-20 lg:py-32 bg-white min-h-screen">
      <SEOHead 
        title="Medical Device Quality Services | ISO 13485 | EU MDR | MedQuore"
        description="Comprehensive medical device quality and regulatory services. Quality assurance, audits, gap assessments, training, and talent acquisition for MedTech companies."
        keywords="medical device services, ISO 13485 consulting, EU MDR compliance, quality assurance services, medical device audits, regulatory consulting"
        canonical="https://medquore.com/services"
        ogTitle="Medical Device Quality Services - MedQuore"
        ogDescription="Expert quality assurance, regulatory compliance, and audit services for medical device companies worldwide."
      />
      <div className="container mx-auto px-4 lg:px-6">
        {/* Page Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center bg-[#f0f0e8] rounded-full px-4 py-2 mb-6">
            <CheckCircle className="w-4 h-4 text-[#c41927] mr-2" />
            <span className="text-sm font-semibold text-[#011431] uppercase tracking-wide">Our Services</span>
          </div>
          <h1 className="text-3xl lg:text-5xl font-bold text-[#011431] mb-6">
            Comprehensive Quality & Regulatory Solutions
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            End-to-end expertise to navigate complex regulatory landscapes and achieve audit-ready quality systems across all major markets.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-8 mb-16">
          {content.services.map((service, index) => {
            const IconComponent = iconComponents[service.icon];
            return (
              <Card key={service.id} id={service.id} className="group hover:shadow-xl transition-all duration-300 border-0 shadow-lg hover:-translate-y-1">
                <CardHeader className="pb-4">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#c41927] to-[#a01520] rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-[#011431] mb-2">{service.title}</h2>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                </CardHeader>
                <CardContent className="pt-0">
                  {service.detailedDescription && (
                    <p className="text-gray-700 text-sm mb-4 leading-relaxed">
                      {service.detailedDescription}
                    </p>
                  )}
                  
                  <div className="mb-4">
                    <h3 className="font-semibold text-[#011431] text-sm mb-2">Key Features:</h3>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center text-sm text-gray-700">
                          <CheckCircle className="w-4 h-4 text-[#c41927] mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {service.benefits && (
                    <div className="mb-6">
                      <h3 className="font-semibold text-[#011431] text-sm mb-2">Benefits:</h3>
                      <ul className="space-y-1">
                        {service.benefits.map((benefit, idx) => (
                          <li key={idx} className="flex items-center text-xs text-gray-600">
                            <div className="w-1.5 h-1.5 bg-[#c41927] rounded-full mr-2 flex-shrink-0"></div>
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <Button 
                    variant="ghost" 
                    onClick={scrollToContact}
                    className="text-[#c41927] hover:bg-[#c41927] hover:text-white group-hover:translate-x-1 transition-all duration-300 p-0 h-auto font-semibold"
                  >
                    Get Started
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="text-center bg-[#f0f0e8] rounded-2xl p-8 lg:p-12">
          <h3 className="text-2xl font-bold text-[#011431] mb-4">
            Ready to achieve audit readiness?
          </h3>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Let's discuss how our expertise can accelerate your compliance journey and reduce time-to-market.
          </p>
          <Button
            onClick={scrollToContact}
            className="bg-[#c41927] hover:bg-[#a01520] text-white font-semibold px-8 py-3 text-lg group transition-all duration-300"
          >
            Schedule a Consultation
            <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Services;